import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, BookOpen, Lock, FileText, Mail, Phone, Eye } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-blue-500 selection:text-white">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-white/5 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white font-bold text-xs uppercase tracking-widest transition-colors">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-1.5 rounded-lg text-white">
              <BookOpen size={18} />
            </div>
            <span className="text-white font-black tracking-tighter text-base">AtronsTutor</span>
          </div>
        </div>
      </nav>

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        {/* Header */}
        <div className="mb-12 border-b border-white/10 pb-8">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 border border-blue-500/20 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
            <Shield size={14} /> Legal & Security
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-400 text-sm font-medium">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-10 text-slate-300 text-sm leading-relaxed">
          
          <section className="bg-slate-900/50 p-6 md:p-8 rounded-3xl border border-white/5 space-y-3">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <FileText size={18} className="text-blue-500" /> 1. Overview
            </h2>
            <p>
              Welcome to <strong>AtronsTutor</strong> ("we," "our," or "us"). We operate the online tutoring platform located at <a href="https://atrons.com.et" className="text-blue-400 underline">https://atrons.com.et</a> and associated mobile applications.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web and mobile applications. Please read this policy carefully. If you do not agree with the terms, please do not access the platform.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">2. Information We Collect</h2>
            <p>We collect personal information that you voluntarily provide to us when registering for an account or using our platform:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 space-y-2">
                <h3 className="font-bold text-blue-400 text-xs uppercase tracking-wider">Student Profile Data</h3>
                <p className="text-xs text-slate-400">Full Name, Email Address, Password, Phone Number, Gender, Location/Address, Grade Level, and School Name.</p>
              </div>

              <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 space-y-2">
                <h3 className="font-bold text-blue-400 text-xs uppercase tracking-wider">Tutor Profile Data</h3>
                <p className="text-xs text-slate-400">Full Name, Email Address, Password, Phone Number, University Name, Field of Major, and uploaded University Identification Document (ID image).</p>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">3. Device Permissions & Sensitive Data Usage</h2>
            <p>To deliver real-time interactive tutoring and seamless deposit verification, our platform requests access to specific device features:</p>
            
            <ul className="list-disc pl-5 space-y-3 text-slate-300">
              <li>
                <strong className="text-white">Camera & Microphone Access:</strong> Required during live tutoring sessions to allow WebRTC video and audio interaction between students and tutors. Camera feeds are never recorded or stored on our servers.
              </li>
              <li>
                <strong className="text-white">SMS Payment Parsing (Android Companion App):</strong> For automated wallet deposits (e.g., Telebirr or CBE), our internal gateway parses payment confirmation receipts strictly to credit user balances. We do not store or inspect non-transactional personal SMS messages.
              </li>
              <li>
                <strong className="text-white">File Uploads (Photos & Media):</strong> Required during tutor onboarding to upload university credentials or when submitting manual bank deposit receipts.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">4. How We Use Your Information</h2>
            <p>We process your data for the following essential platform purposes:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-300">
              <li>Creating and managing student and tutor accounts.</li>
              <li>Verifying tutor credentials and academic eligibility.</li>
              <li>Matching students with qualified tutors for live sessions.</li>
              <li>Processing wallet deposits, tracking billing per session, and paying out tutor earnings.</li>
              <li>Ensuring platform security, preventing fraud, and enforcing terms of service.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">5. Data Sharing and Third Parties</h2>
            <p>
              We do <strong>not</strong> sell, rent, or trade your personal information to advertisers or third parties. We may share information only in the following scenarios:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-slate-300">
              <li><strong className="text-white">Legal Obligations:</strong> If required by Ethiopian law enforcement or regulatory authorities.</li>
              <li><strong className="text-white">Service Infrastructure:</strong> With cloud hosting and database providers supporting app operation under strict confidentiality agreements.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">6. Data Security & Retention</h2>
            <p>
              We implement industry-standard encryption protocols (SSL/HTTPS and password hashing) to protect your personal data. Your information is retained for as long as your account remains active on AtronsTutor.
            </p>
          </section>

          <section className="bg-blue-950/40 p-6 md:p-8 rounded-3xl border border-blue-500/20 space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Eye size={20} className="text-blue-400" /> 7. Your Rights & Account Deletion
            </h2>
            <p>
              You have the right to request access to your stored data or request the permanent deletion of your profile and personal information at any time.
            </p>
            <p className="text-xs text-slate-400">
              To request full account or data deletion, please email our support team at <strong className="text-white">support@atrons.com.et</strong> with your registered email address. Requests are processed within 48 hours.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">8. Contact Us</h2>
            <p>If you have any questions or concerns regarding this Privacy Policy, please contact us:</p>
            <div className="bg-slate-900 p-6 rounded-2xl border border-white/5 space-y-2 text-xs">
              <p className="flex items-center gap-2 font-bold text-white"><BookOpen size={16} className="text-blue-500" /> AtronsTutor Ethiopia</p>
              <p className="flex items-center gap-2 text-slate-400"><Mail size={16} /> Email: support@atrons.com.et</p>
              <p className="flex items-center gap-2 text-slate-400"><Phone size={16} /> Website: https://atrons.com.et</p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 text-center text-slate-600 text-xs font-bold uppercase tracking-widest">
        © {new Date().getFullYear()} AtronsTutor. All Rights Reserved.
      </footer>
    </div>
  );
};

export default PrivacyPolicy;